import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import {
  applyAudioOutputDevice,
  readStoredAudioOutputDeviceId,
} from "./liveInterviewAudioOutput";

let playToken = 0;
let activeSynthesizer = null;
let activeAudio = null;
let activeUtterance = null;
let activeObjectUrl = "";

function azureSpeechReady(key, region) {
  return Boolean(String(key || "").trim() && String(region || "").trim());
}

function resolveAzureVoiceName(language) {
  const lang = String(language || "en-IN").trim() || "en-IN";
  if (/^en[-_]IN/i.test(lang)) return "en-IN-NeerjaNeural";
  if (/^en[-_]GB/i.test(lang)) return "en-GB-SoniaNeural";
  if (/^en/i.test(lang)) return "en-US-JennyNeural";
  return "en-IN-NeerjaNeural";
}

function closeSynthesizer(synthesizer) {
  if (!synthesizer) return;
  try {
    synthesizer.close();
  } catch {
    // Best-effort only.
  }
}

export function stopInterviewerFeedbackTts() {
  playToken += 1;
  const synthesizer = activeSynthesizer;
  activeSynthesizer = null;
  closeSynthesizer(synthesizer);

  if (activeAudio) {
    try {
      activeAudio.pause();
      activeAudio.removeAttribute("src");
      activeAudio.load?.();
    } catch {
      // Best-effort only.
    }
    activeAudio = null;
  }

  if (activeObjectUrl) {
    try {
      URL.revokeObjectURL(activeObjectUrl);
    } catch {
      // Best-effort only.
    }
    activeObjectUrl = "";
  }

  activeUtterance = null;
  try {
    window.speechSynthesis?.cancel?.();
    window.speechSynthesis?.resume?.();
  } catch {
    // Best-effort only.
  }
}

function isCurrentPlay(token) {
  return token === playToken;
}

async function applySelectedSpeaker(element) {
  const deviceId = readStoredAudioOutputDeviceId();
  if (!deviceId || !element) return;
  try {
    await applyAudioOutputDevice(deviceId, element);
  } catch {
    // Browser may not allow sink selection; default output still plays.
  }
}

function waitForAudioEnd(audio, token) {
  return new Promise((resolve) => {
    const finish = (ok) => {
      audio.onended = null;
      audio.onerror = null;
      resolve(ok && isCurrentPlay(token));
    };
    audio.onended = () => finish(true);
    audio.onerror = () => finish(false);
  });
}

async function playAudioBuffer(audioData, token) {
  if (!audioData || audioData.byteLength < 44 || typeof Audio === "undefined") {
    return false;
  }
  const blob = new Blob([audioData], { type: "audio/wav" });
  const objectUrl = URL.createObjectURL(blob);
  activeObjectUrl = objectUrl;
  const audio = new Audio(objectUrl);
  audio.volume = 1;
  activeAudio = audio;
  await applySelectedSpeaker(audio);
  if (!isCurrentPlay(token)) return false;
  try {
    await audio.play();
  } catch (error) {
    console.warn("[InterviewerFeedback] Azure TTS element playback failed", error);
    return false;
  }
  return waitForAudioEnd(audio, token);
}

function speakWithAzureSpeaker(text, key, region, language, token) {
  return new Promise((resolve) => {
    if (
      !SpeechSDK?.SpeechSynthesizer ||
      !SpeechSDK?.SpeechConfig ||
      typeof SpeechSDK.AudioConfig?.fromDefaultSpeakerOutput !== "function"
    ) {
      resolve(false);
      return;
    }
    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(key, region);
      speechConfig.speechSynthesisLanguage = String(language || "en-IN").trim() || "en-IN";
      speechConfig.speechSynthesisVoiceName = resolveAzureVoiceName(language);
      const audioConfig =
        typeof SpeechSDK.AudioConfig?.fromDefaultSpeakerOutput === "function"
          ? SpeechSDK.AudioConfig.fromDefaultSpeakerOutput()
          : null;
      const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, audioConfig);
      activeSynthesizer = synthesizer;
      synthesizer.speakTextAsync(
        text,
        (result) => {
          closeSynthesizer(synthesizer);
          if (activeSynthesizer === synthesizer) activeSynthesizer = null;
          const completed =
            !SpeechSDK.ResultReason ||
            result?.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted ||
            Boolean(result?.audioData);
          resolve(Boolean(completed && isCurrentPlay(token)));
        },
        (error) => {
          console.warn("[InterviewerFeedback] Azure speaker TTS failed", error);
          closeSynthesizer(synthesizer);
          if (activeSynthesizer === synthesizer) activeSynthesizer = null;
          resolve(false);
        }
      );
    } catch (error) {
      console.warn("[InterviewerFeedback] Azure speaker TTS init failed", error);
      resolve(false);
    }
  });
}

function speakWithAzureAudioData(text, key, region, language, token) {
  return new Promise((resolve) => {
    if (!SpeechSDK?.SpeechSynthesizer || !SpeechSDK?.SpeechConfig) {
      resolve(false);
      return;
    }
    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(key, region);
      speechConfig.speechSynthesisLanguage = String(language || "en-IN").trim() || "en-IN";
      speechConfig.speechSynthesisVoiceName = resolveAzureVoiceName(language);
      const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, null);
      activeSynthesizer = synthesizer;
      synthesizer.speakTextAsync(
        text,
        (result) => {
          closeSynthesizer(synthesizer);
          if (activeSynthesizer === synthesizer) activeSynthesizer = null;
          if (!isCurrentPlay(token)) {
            resolve(false);
            return;
          }
          const data = result?.audioData;
          if (!data || data.byteLength < 44) {
            resolve(false);
            return;
          }
          void playAudioBuffer(data, token).then((played) => {
            resolve(played);
          });
        },
        (error) => {
          console.warn("[InterviewerFeedback] Azure audio TTS failed", error);
          closeSynthesizer(synthesizer);
          if (activeSynthesizer === synthesizer) activeSynthesizer = null;
          resolve(false);
        }
      );
    } catch (error) {
      console.warn("[InterviewerFeedback] Azure audio TTS init failed", error);
      resolve(false);
    }
  });
}

function speakWithBrowserTts(text, language, token) {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      resolve(false);
      return;
    }
    const voices = window.speechSynthesis.getVoices?.() || [];
    const preferredVoice =
      voices.find((voice) => /^en[-_]IN$/i.test(voice.lang || "")) ||
      voices.find((voice) => /^en[-_]US$/i.test(voice.lang || "")) ||
      voices.find((voice) => /^en/i.test(voice.lang || "")) ||
      null;

    try {
      window.speechSynthesis.cancel();
      window.speechSynthesis.resume?.();
    } catch {
      // Continue with a fresh utterance.
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.volume = 1;
    utterance.rate = 0.92;
    utterance.pitch = 1;
    utterance.lang = preferredVoice?.lang || language || navigator.language || "en-IN";
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    activeUtterance = utterance;

    let started = false;
    const resumeTimer = window.setInterval(() => {
      if (!isCurrentPlay(token)) {
        window.clearInterval(resumeTimer);
        return;
      }
      if (window.speechSynthesis.speaking && window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
    }, 2500);

    const finish = (ok) => {
      window.clearInterval(resumeTimer);
      if (activeUtterance === utterance) activeUtterance = null;
      resolve(Boolean(ok && isCurrentPlay(token) && started));
    };

    utterance.onstart = () => {
      started = true;
    };
    utterance.onend = () => finish(true);
    utterance.onerror = () => finish(false);

    window.setTimeout(() => {
      if (!isCurrentPlay(token)) {
        finish(false);
        return;
      }
      try {
        window.speechSynthesis.speak(utterance);
        window.speechSynthesis.resume?.();
      } catch (error) {
        console.warn("[InterviewerFeedback] Browser TTS failed", error);
        finish(false);
      }
    }, 50);
  });
}

export async function playInterviewerFeedbackTts({
  text,
  azureKey,
  azureRegion,
  language = "en-IN",
} = {}) {
  const prompt = String(text || "").replace(/\s+/g, " ").trim();
  if (!prompt) return false;

  stopInterviewerFeedbackTts();
  const token = playToken;

  if (azureSpeechReady(azureKey, azureRegion)) {
    const playedThroughElement = await speakWithAzureAudioData(
      prompt,
      azureKey,
      azureRegion,
      language,
      token
    );
    if (playedThroughElement) return true;
    if (!isCurrentPlay(token)) return false;

    const playedThroughSpeaker = await speakWithAzureSpeaker(
      prompt,
      azureKey,
      azureRegion,
      language,
      token
    );
    if (playedThroughSpeaker) {
      const deviceId = readStoredAudioOutputDeviceId();
      if (deviceId && typeof document !== "undefined") {
        document.querySelectorAll("audio, video").forEach((node) => {
          void applyAudioOutputDevice(deviceId, node);
        });
      }
      return true;
    }
    if (!isCurrentPlay(token)) return false;
  }

  return speakWithBrowserTts(prompt, language, token);
}
